import { yieldBetweenMemberCompiles } from './compile-task';

export interface TreePart { kind: 'tree-part'; tag: string; sequence: number; entries: { path: string[]; value: unknown; merge?: boolean; reference?: string[] }[]; done: boolean }
function putOwn(target: object, key: string, value: unknown): void {
    if (key === '__proto__') Object.defineProperty(target, key, { value, writable: true, enumerable: true, configurable: true });
    else (target as Record<string, unknown>)[key] = value;
}
/** Bound structured-clone work per message. A large raw catalog must not become
 * a single main-thread serialization/deserialization task. Values stay values,
 * including omitted keys, empty arrays, null, zero and own __proto__ fields. */
export async function postTree(send: (part: TreePart) => void, tag: string, sequence: number, value: unknown,
    current: () => boolean = () => true, entriesPerPart = 32): Promise<boolean> {
    const stack: { path: string[]; value: unknown; keys?: string[]; offset?: number }[] = [{ path: [], value }];
    const seen = new WeakMap<object, string[]>();
    while (stack.length) {
        if (!current()) return false;
        const entries: TreePart['entries'] = [];
        for (let count = 0; stack.length && count < entriesPerPart; count++) {
            const { path, value, keys: knownKeys, offset = 0 } = stack.pop()!;
            if (value === null || typeof value !== 'object') { entries.push({ path, value }); continue; }
            if (!knownKeys) {
                const previous = seen.get(value);
                if (previous) { entries.push({ path, value: null, reference: previous }); continue; }
                seen.set(value, path);
            }
            const shallow: Record<string, unknown> = Array.isArray(value) ? new Array(value.length) as unknown as Record<string, unknown> : {};
            const keys = knownKeys ?? Object.keys(value), end = Math.min(keys.length, offset + 64);
            if (end < keys.length) stack.push({ path, value, keys, offset: end });
            for (let index = offset; index < end; index++) {
                const key = keys[index]!;
                const child = (value as Record<string, unknown>)[key];
                if (child !== null && typeof child === 'object') stack.push({ path: [...path, key], value: child });
                // Reserve every key in source order. Appending object children as
                // later parts arrive would reorder PHP layer/provider buckets.
                putOwn(shallow, key, child !== null && typeof child === 'object' ? null : child);
            }
            entries.push({ path, value: shallow, merge: offset > 0 });
        }
        send({ kind: 'tree-part', tag, sequence, entries, done: stack.length === 0 });
        if (stack.length) await yieldBetweenMemberCompiles();
    }
    return true;
}

/** An owned JSON tree without a monolithic main-thread clone. */
export async function copyTree(value: unknown, omitKeys?: ReadonlySet<string>): Promise<unknown> {
    if (value === null || typeof value !== 'object') return value;
    const copies = new WeakMap<object, object>();
    const stack: { source: object; target: object; keys: string[]; offset: number }[] = [];
    const own = (source: object): object => {
        const previous = copies.get(source);
        if (previous) return previous;
        const target = Array.isArray(source) ? new Array(source.length) : {};
        copies.set(source, target);
        stack.push({ source, target, keys: Object.keys(source), offset: 0 });
        return target;
    };
    const copy = own(value);
    while (stack.length) {
        for (let count = 0; stack.length && count < 1024; count++) {
            const frame = stack[stack.length - 1]!;
            if (frame.offset === frame.keys.length) { stack.pop(); continue; }
            const key = frame.keys[frame.offset++]!, child = (frame.source as Record<string, unknown>)[key];
            if (omitKeys?.has(key)) continue;
            putOwn(frame.target, key, child !== null && typeof child === 'object' ? own(child) : child);
        }
        if (stack.length) await yieldBetweenMemberCompiles();
    }
    return copy;
}

/** Compare saved JSON without stringifying an entire encounter on the page. */
export async function sameTree(left: unknown, right: unknown): Promise<boolean> {
    const stack: [unknown, unknown][] = [[left, right]];
    const seen = new WeakMap<object, WeakSet<object>>();
    let remaining = 1024;
    while (stack.length) {
        const [a, b] = stack.pop()!;
        if (a !== b) {
            if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object'
                || Array.isArray(a) !== Array.isArray(b)) return false;
            if (Array.isArray(a) && a.length !== (b as unknown[]).length) return false;
            const previous = seen.get(a);
            if (!previous?.has(b)) {
                const pairs = previous ?? new WeakSet<object>(); pairs.add(b); seen.set(a, pairs);
                const keys = Object.keys(a), otherKeys = Object.keys(b);
                if (keys.length !== otherKeys.length || keys.some((key, index) => key !== otherKeys[index])) return false;
                for (const key of keys) stack.push([(a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]]);
            }
        }
        if (--remaining === 0 && stack.length) { remaining = 1024; await yieldBetweenMemberCompiles(); }
    }
    return true;
}

export function createTreeReceiver() {
    const pending = new Map<string, unknown>();
    const keyOf = (part: Pick<TreePart, 'tag' | 'sequence'>) => `${part.tag}:${part.sequence}`;
    function receive(part: TreePart): { value: unknown } | undefined {
        const key = keyOf(part);
        for (const { path, value: sentValue, merge, reference } of part.entries) {
            let value = sentValue;
            if (reference) {
                value = pending.get(key);
                for (const name of reference) value = (value as Record<string, unknown>)[name];
            }
            if (!path.length) {
                if (merge) for (const name of Object.keys(value as object)) putOwn(pending.get(key) as object, name, (value as Record<string, unknown>)[name]);
                else pending.set(key, value);
                continue;
            }
            let parent = pending.get(key) as Record<string, unknown>;
            for (let index = 0; index < path.length - 1; index++) parent = parent[path[index]!] as Record<string, unknown>;
            const name = path[path.length - 1]!;
            if (merge) {
                const target = parent[name] as object;
                for (const child of Object.keys(value as object)) putOwn(target, child, (value as Record<string, unknown>)[child]);
            } else putOwn(parent, name, value);
        }
        if (!part.done) return;
        const value = pending.get(key); pending.delete(key); return { value };
    }
    return { receive, discard: (tag: string, sequence: number) => pending.delete(`${tag}:${sequence}`) };
}
